<template>
  <div class="sp-hero-slideshow-navigation">
    <sp-hero-slideshow-navigation-item
      v-for="(slide, index) in slides"
      :key="index"
      :interval="getSlideInterval(slide)"
      :active="index === current"
      @click="handleItemClick(index)"
    ></sp-hero-slideshow-navigation-item>
  </div>
</template>

<script>
const DEFAULT_INTERVAL = 3000;
const MIN_INTERVAL = 1000;
const MAX_INTERVAL = 10000;
</script>

<script setup>
import { computed, onMounted, onUnmounted } from "vue";
import { booleanAttribute, toBoolean, toNumber } from "#ce/utils/props";

const emit = defineEmits(["update-current"]);

const props = defineProps({
  slides: {
    type: Array,
    required: true,
  },
  current: {
    type: Number,
    required: true,
  },
  interval: {
    type: [Number, String],
    default: DEFAULT_INTERVAL,
  },
  autoplay: booleanAttribute(),
});

const autoplay = computed(() => toBoolean(props.autoplay));
let timer;

const interval = computed(() => {
  const value = toNumber(props.interval, DEFAULT_INTERVAL);
  return Math.min(Math.max(value, MIN_INTERVAL), MAX_INTERVAL);
});

function getSlideInterval(slide) {
  return slide.interval ?? interval.value;
}

function goToSlide(index) {
  emit("update-current", index);
}

function handleItemClick(index) {
  stopAutoplay();
  goToSlide(index);
  startAutoplay();
}

function advanceSlide() {
  goToSlide((props.current + 1) % props.slides.length);
}

function startAutoplay() {
  if (autoplay.value) {
    timer = setInterval(advanceSlide, interval.value);
  }
}

function stopAutoplay() {
  clearInterval(timer);
  timer = undefined;
}

onMounted(startAutoplay);
onUnmounted(stopAutoplay);
</script>

<style>
:host {
  display: block;
  position: absolute;
  bottom: var(--slideshow-navigation-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}
</style>

<style scoped lang="scss">
.sp-hero-slideshow-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-ref-scale-8);
  height: var(--sp-comp-hero-slideshow-navigation-height, var(--slideshow-navigation-height, 2.625rem));
  padding: var(--sp-comp-hero-slideshow-navigation-padding, 1rem);
  background: var(--sp-comp-hero-slideshow-navigation-background, rgba(0, 0, 0, 0.2));
  border-radius: var(--sp-comp-hero-slideshow-navigation-border-radius, 7.5rem);
  backdrop-filter: var(--sp-comp-hero-slideshow-navigation-backdrop-filter, blur(4px));
  box-sizing: border-box;
}
</style>
